import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import { useAuth0 } from '@auth0/auth0-react';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useEmailComponentFunctions from 'components/Dashboard/Influence/CrmEmails/useEmailComponentFunctions';
import { useLocation, useNavigate } from 'react-router';

function useMailMergeFunctions() {
  const { generalApiCall } = useGeneralApiCall();
  const { getAccessToken } = useGetAccessToken();
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const source = new AbortController();

  const { saveEmail, emailIsNotValid } = useEmailComponentFunctions();
  const createEmailPath = location.pathname.includes('/emails/create');

  const downloadMailMergeCall = async ({ id }) => {
    try {
      const result = await generalApiCall({
        method: 'get',
        pathname: `/api/crm-email/mail-merge-data/${id}`,
        needsAuthentication: true,
        requestSource: source,
        responseType: 'text',
        returnCompleteRequest: true,
        notShowErrorMessage: true,
      });
      if (result?.status === 200) {
        return result.data;
      } else {
        createNotification('danger', 'An error occured');
      }
    } catch (error) {
      createNotification('danger', 'An error occured');
    }
  };

  const downloadMailMerge = async ({ id }) => {
    const result = await downloadMailMergeCall({ id });
    if (result) {
      // Add BOM for Excel compatibility
      const blob = new Blob(['\uFEFF' + result], { type: 'text/csv;charset=utf-8;' });
      const blobURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobURL;
      link.download = 'Mail merge data.csv';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobURL);
    }
  };

  const uploadMailMerge = async ({ id, csvData }) => {
    try {
      const token = isAuthenticated ? await getAccessToken() : '';
      const result = await generalApiCall({
        method: 'post',
        pathname: `/api/crm-email/upload-mail-merge-data?email-id=${id}`,
        needsAuthentication: false, // We're handling auth manually
        requestSource: source,
        requestProperties: csvData,
        requestHeaders: {
          signal: source.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'text/csv',
          },
        },
        returnCompleteRequest: true,
      });
      if (result) {
        return result;
      }
    } catch (error) {}
  };

  const parseCSV = (csvText) => {
    if (!csvText || !csvText.trim()) {
      return { headers: [], rows: [] };
    }

    const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length === 0) {
      return { headers: [], rows: [] };
    }

    // Simple CSV parser that handles quoted fields
    const parseLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i++; // Skip next quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseLine(lines[0]);
    const rows = lines.slice(1).map((line) => parseLine(line));

    return { headers, rows };
  };

  const viewMailMerge = async ({ id }) => {
    try {
      const result = await generalApiCall({
        method: 'get',
        pathname: `/api/crm-email/mail-merge-data/${id}`,
        needsAuthentication: true,
        requestSource: source,
        responseType: 'text',
      });
      if (result) {
        const parsed = parseCSV(result);
        return parsed;
      }
    } catch (error) {}
  };

  const clearMailMerge = async ({ id }) => {
    try {
      const result = await generalApiCall({
        method: 'delete',
        pathname: `/api/crm-email/mail-merge-data/${id}`,
        needsAuthentication: true,
        requestSource: source,
        returnCompleteRequest: true,
      });
      if (result) {
        return result;
      }
    } catch (error) {}
  };

  const autoSaveEmailOnMailMerge = async ({ action, callback, setIsLoading }) => {
    if (emailIsNotValid({ mailMerge: true })) {
      return createNotification('danger', emailIsNotValid({ mailMerge: true }));
    } else {
      if (createEmailPath) {
        setIsLoading(true);
        await saveEmail({
          isForSending: false,
          notRedirect: true,
          actionOnSuccess: (email) => {
            navigate(`/people/emails/${email.crmEmailId}`, {
              state: { [`open${action}`]: true },
              replace: true,
            });
          },
          loadingType: 'preview',
          preview: true,
        });
        setIsLoading(false);
      } else {
        callback();
      }
    }
  };

  return {
    downloadMailMerge,
    uploadMailMerge,
    viewMailMerge,
    clearMailMerge,
    autoSaveEmailOnMailMerge,
  };
}

export default useMailMergeFunctions;
